import {
  Button,
  Link as ChakraLink,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Flex,
  Box,
  Image,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Text,
  VStack,
  IconButton,
  Badge,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { SearchIcon, ChatIcon } from "@chakra-ui/icons";
import SideNav from "./SideNav";
import userProfileImage from "../../assets/user-profile.jpg";
import "../../css/NavBar.css";
import {
  getSupabase,
  explainSupabaseError,
} from "../../components/supabaseClient";
import {
  getDefaultDateString,
  logo,
  navy_blue,
  tableName,
} from "../env";
import CustomCalendar from "../CustomCalendar";
import { getNavbarItems, isPatientRole } from "../../config/roles";
import { loadCurrentStaffUser } from "../../utils/currentStaffUser";
import { openMessages, onMessagesClosed } from "../../utils/messagingBus";
import { useMessagingNotifications, refreshUnread } from "../../utils/messagingNotifications";

const NavBar = () => {
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [role, setRole] = useState(null);
  const [uname, setUname] = useState(null);
  const [date, setDate] = useState(getDefaultDateString());
  const [patientCount, setPatientCount] = useState(0);
  const [schema, setSchema] = useState(null);
  const [authUser, setAuthUser] = useState(null); // { id, email } from the Supabase session
  // The app-wide unread summary (utils/messagingNotifications.js, started by MessagingHost for
  // staff only): its socket and slow poll keep it current, so the Navbar fetches nothing itself.
  const { summary } = useMessagingNotifications();

  const navigate = useNavigate();
  const location = useLocation();

  const renderLink = (item) => (
    <ChakraLink
      key={item.pageKey}
      as={RouterLink}
      to={item.path}
      mr={4}
      fontSize="lg"
      color="white"
    >
      {item.label}
    </ChakraLink>
  );

  // Lazy: Chakra otherwise renders PopoverContent up front, so every calendar
  // item mounted a hidden CustomCalendar that read a month of visits on every
  // page. Kept mounted once opened, so reopening shows the month and counts
  // that were left there, as before.
  const renderCalendarPopover = (item) => (
    <Popover key={item.pageKey} trigger="click" isLazy lazyBehavior="keepMounted">
      <PopoverTrigger>
        <Text
          as="span"
          cursor="pointer"
          mr={4}
          fontSize="lg"
          color="white"
          _hover={{ textDecoration: "underline" }}
        >
          {item.label}
        </Text>
      </PopoverTrigger>
      <PopoverContent zIndex={9999} bg="white" boxShadow="xl">
        <CustomCalendar
          date={date}
          onSelectDate={(newDate) => {
            setDate(newDate);
            navigate(`${item.path}?date=${newDate}`);
          }}
        />
      </PopoverContent>
    </Popover>
  );

  const renderNavItem = (item) => {
    switch (item.kind) {
      case "link": return renderLink(item);
      case "calendarPopover": return renderCalendarPopover(item);
      default: return renderLink(item);
    }
  };

  const toggleSideNav = () => setIsSideNavOpen(!isSideNavOpen);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/search?query=${searchQuery}`, { q: searchQuery });
      setSearchQuery("");
    }
  };

  useEffect(() => {
    // On /403, don't fetch role — render navbar like /auth (logo only, no nav links)
    if (location.pathname === "/403") return;
    const fetchUserRole = async () => {
      const me = await loadCurrentStaffUser();
      if (me) {
        setAuthUser({ id: me.id, email: me.email });
        setRole(me.role);
        setUname(me.name);
        setSchema(me.schema);
      }
    };
    fetchUserRole();
  }, [location.pathname]);

  // The Messages modal lives in the app-wide MessagingHost; refresh the
  // unread badge when it says it closed (a no-op while the store isn't running,
  // e.g. for a patient: Messages is staff-only).
  useEffect(() => onMessagesClosed(() => refreshUnread()), []);
  // Slack's badge: the number is mentions and DMs; other unread messages are a dot.
  const mentionTotal = summary.mention_total || 0;
  const unreadDot = mentionTotal === 0 && (summary.total || 0) > 0;

  const fetchPatientCount = async (date) => {
    const supabase = await getSupabase();
    // Only the count is used: head asks for it without the rows, so the PHI
    // overlay has no rows to fetch again from RDS.
    const { count, error } = await supabase
      .from(tableName)
      .select("id", { count: "exact", head: true })
      .eq("date_", date);
    if (error) {
      explainSupabaseError(error, "fetchPatientCount");
      setPatientCount(0);
    } else {
      setPatientCount(count);
    }
  };

  useEffect(() => {
    if (date) fetchPatientCount(date);
  }, [date]);

  // Don't render the navbar on the homepage or on the medstudent clinic page
  // (the clinic page is full-screen with its own navigation rail).
  if (location.pathname === "/") return null;
  if (location.pathname.startsWith("/clinic/")) return null;

  return (
    <>
      <Flex
        className="navbar"
        as="nav"
        backgroundColor={navy_blue}
        position="relative"
      >
        <Box display="flex" alignItems="center">
          <ChakraLink as={RouterLink} to="/">
            {logo && (
              <Image
                src={logo}
                alt="Logo"
                boxSize="64px"
                cursor="pointer"
                objectFit="contain"
              />
            )}
          </ChakraLink>

          {/* Nav items derived from roles config — order/visibility set in shared/roles.json.
              Patients use the in-portal nav (Home/Visits/Meds…), so the top-bar
              clinic nav items (Schedules, etc.) are hidden for them. */}
          {role && !isPatientRole(role) && getNavbarItems(role).map(renderNavItem)}
        </Box>

        <Box ml="auto" display="flex" alignItems="center">
          {!isPatientRole(role) && (
            <Button
              onClick={() => navigate("/search")}
              colorScheme="blue"
              size="md"
              borderRadius="md"
              marginRight={4}
            >
              Go to Search
            </Button>
          )}
          <VStack spacing={0} mr={4} align="center">
            <Text whiteSpace="nowrap" textAlign="center" color="white">
              {uname || "Loading..."}
            </Text>
            {role && (
              <Text fontSize="sm" color="gray.300" textAlign="center">
                Role: {role}
              </Text>
            )}
            <Text fontSize="sm" color="gray.300" textAlign="center">
              Schema: {schema}
            </Text>
          </VStack>
          {!isPatientRole(role) && authUser && (
            <Box position="relative" mr={4}>
              <IconButton
                aria-label="Messages"
                icon={<ChatIcon />}
                onClick={() => openMessages()}
                variant="ghost"
                color="white"
                _hover={{ bg: "whiteAlpha.300" }}
              />
              {mentionTotal > 0 && (
                <Badge
                  colorScheme="red"
                  variant="solid"
                  borderRadius="full"
                  position="absolute"
                  top="-1"
                  right="-1"
                  fontSize="xs"
                  pointerEvents="none"
                >
                  {mentionTotal > 99 ? "99+" : mentionTotal}
                </Badge>
              )}
              {unreadDot && (
                <Box
                  role="img"
                  aria-label="Unread messages"
                  data-testid="messages-unread-dot"
                  position="absolute"
                  top="1"
                  right="1"
                  w="9px"
                  h="9px"
                  borderRadius="full"
                  bg="red.400"
                  border="1.5px solid white"
                  pointerEvents="none"
                />
              )}
            </Box>
          )}
          <Image
            boxSize="40px"
            src={userProfileImage}
            alt="User Profile"
            className="user-profile"
            onClick={toggleSideNav}
          />
        </Box>
      </Flex>

      <Drawer isOpen={isSideNavOpen} placement="right" onClose={toggleSideNav}>
        <DrawerOverlay>
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerBody>
              <SideNav />
            </DrawerBody>
          </DrawerContent>
        </DrawerOverlay>
      </Drawer>
    </>
  );
};

export default NavBar;
